import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, ChevronLeft, ChevronRight, Grid3x3, Flag, X } from "lucide-react";
import Btn from "../components/Btn";
import api, { apiErr } from "../lib/api";
import { QStimulus, OptMedia, hasMedia } from "../components/QMedia";

export default function CBTSimulator() {
  const { attemptId } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const data = loc.state;

  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState({});
  const [showPalette, setShowPalette] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const startRef = useRef(Date.now());

  const totalSecs = (data?.package?.duration_minutes || 20) * 60;
  const [left, setLeft] = useState(totalSecs);

  const questions = data?.questions || [];
  const q = questions[idx];

  const doSubmit = async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        attempt_id: attemptId,
        answers: questions.map((qq) => ({ qid: qq.qid, answer: answers[qq.qid] || null })),
        time_spent_seconds: Math.round((Date.now() - startRef.current) / 1000),
      };
      const { data: res } = await api.post("/cbt/submit", payload);
      if (auto) toast.info("Waktu habis! Jawaban otomatis dikumpulkan.");
      nav(`/result/${attemptId}`, { replace: true, state: res });
    } catch (e) {
      toast.error(apiErr(e));
      setSubmitting(false);
    }
  };
  const submitRef = useRef(doSubmit);
  submitRef.current = doSubmit;

  useEffect(() => {
    if (!data) return;
    const id = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) { clearInterval(id); submitRef.current(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [data]);

  const answeredCount = useMemo(() => Object.values(answers).filter(Boolean).length, [answers]);

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-su-bg px-4 text-center">
        <div>
          <p className="font-heading font-black text-xl mb-3">Sesi tryout tidak ditemukan</p>
          <Btn onClick={() => nav("/tryouts")}>Kembali ke Tryout</Btn>
        </div>
      </div>
    );
  }

  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  const danger = left < 300;

  const select = (key) => setAnswers((a) => ({ ...a, [q.qid]: key }));

  return (
    <div className="min-h-screen bg-su-bg">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-su-surface border-b-2 border-su-border">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading font-black truncate">{data.package.title}</p>
            <p className="text-xs text-su-muted font-bold">{answeredCount}/{questions.length} terjawab</p>
          </div>
          <div className={`flex items-center gap-2 font-mono font-bold text-xl px-4 py-2 rounded-2xl ${danger ? "bg-su-danger/15 text-su-danger animate-pulse" : "bg-su-primary/10 text-su-primary"}`} data-testid="cbt-timer">
            <Clock className="w-5 h-5" /> {mm}:{ss}
          </div>
          <button onClick={() => setShowPalette(true)} data-testid="cbt-palette-toggle" className="lg:hidden w-11 h-11 rounded-2xl border-2 border-su-border grid place-items-center">
            <Grid3x3 className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid lg:grid-cols-4 gap-6">
        {/* question */}
        <div className="lg:col-span-3">
          <div className="card-chunky p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-heading font-black uppercase tracking-wider text-su-primary">{q.category} · {q.subcategory}</span>
              <button data-testid="cbt-flag"
                onClick={() => setFlags((f) => ({ ...f, [q.qid]: !f[q.qid] }))}
                className={`flex items-center gap-1 text-xs font-heading font-black px-3 py-1.5 rounded-full ${flags[q.qid] ? "bg-su-energy/15 text-su-energy" : "bg-su-track text-su-muted"}`}>
                <Flag className="w-3.5 h-3.5" /> {flags[q.qid] ? "Ditandai" : "Tandai"}
              </button>
            </div>
            <h2 className="font-heading font-extrabold text-lg sm:text-xl mb-6 whitespace-pre-line leading-relaxed" data-testid="cbt-question-text">
              <span className="text-su-muted mr-2">{idx + 1}.</span>{q.question_text}
            </h2>
            <QStimulus q={q} />

            <div className="space-y-3">
              {q.options.map((opt) => {
                const sel = answers[q.qid] === opt.key;
                return (
                  <button
                    key={opt.key}
                    data-testid={`cbt-option-${opt.key}`}
                    onClick={() => select(opt.key)}
                    className={`w-full text-left flex items-center gap-4 rounded-2xl border-2 p-4 transition-all ${
                      sel ? "border-su-primary bg-su-primary/10" : "border-su-border hover:border-su-primary/50"
                    }`}
                  >
                    <span className={`w-9 h-9 shrink-0 grid place-items-center rounded-xl font-heading font-black ${sel ? "bg-su-primary text-white" : "bg-su-track text-su-muted"}`}>{opt.key}</span>
                    {hasMedia(opt) ? <OptMedia opt={opt} /> : <span className="font-medium">{opt.text}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* nav buttons */}
          <div className="flex items-center justify-between mt-5">
            <Btn variant="ghost" data-testid="cbt-prev" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
              <ChevronLeft className="w-5 h-5" /> Sebelumnya
            </Btn>
            {idx < questions.length - 1 ? (
              <Btn data-testid="cbt-next" onClick={() => setIdx((i) => Math.min(questions.length - 1, i + 1))}>
                Berikutnya <ChevronRight className="w-5 h-5" />
              </Btn>
            ) : (
              <Btn variant="success" data-testid="cbt-submit" disabled={submitting} onClick={() => doSubmit(false)}>
                {submitting ? "Mengoreksi..." : "Selesai & Koreksi"}
              </Btn>
            )}
          </div>
        </div>

        {/* palette desktop */}
        <div className="hidden lg:block">
          <Palette {...{ questions, idx, setIdx, answers, flags }} onSubmit={() => doSubmit(false)} submitting={submitting} answeredCount={answeredCount} />
        </div>
      </div>

      {/* palette mobile drawer */}
      {showPalette && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" onClick={() => setShowPalette(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full bg-su-surface rounded-t-3xl p-5 max-h-[75vh] overflow-auto animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-black text-lg">Navigasi Soal</h3>
              <button onClick={() => setShowPalette(false)} className="w-9 h-9 rounded-xl border-2 border-su-border grid place-items-center"><X className="w-5 h-5" /></button>
            </div>
            <Palette {...{ questions, idx, setIdx: (i) => { setIdx(i); setShowPalette(false); }, answers, flags }} onSubmit={() => doSubmit(false)} submitting={submitting} answeredCount={answeredCount} />
          </div>
        </div>
      )}
    </div>
  );
}

function Palette({ questions, idx, setIdx, answers, flags, onSubmit, submitting, answeredCount }) {
  return (
    <div className="card-chunky p-5 lg:sticky lg:top-24">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-black">Peta Soal</h3>
        <span className="text-xs font-heading font-black text-su-muted">{answeredCount}/{questions.length}</span>
      </div>
      <div className="grid grid-cols-6 lg:grid-cols-5 gap-2 mb-4">
        {questions.map((qq, i) => {
          const answered = !!answers[qq.qid];
          const flagged = flags[qq.qid];
          const active = i === idx;
          return (
            <button
              key={qq.qid}
              data-testid={`palette-${i}`}
              onClick={() => setIdx(i)}
              className={`aspect-square rounded-xl font-mono font-bold text-sm grid place-items-center border-2 transition-all ${
                active ? "border-su-primary ring-2 ring-su-primary/40" : "border-transparent"
              } ${flagged ? "bg-su-energy/20 text-su-energy" : answered ? "bg-su-success text-white" : "bg-su-track text-su-muted"}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[11px] font-bold text-su-muted mb-4">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-su-success inline-block" /> Terjawab</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-su-energy/40 inline-block" /> Ditandai</span>
      </div>
      <Btn variant="success" className="w-full" disabled={submitting} onClick={onSubmit} data-testid="palette-submit">
        {submitting ? "Mengoreksi..." : "Selesai & Koreksi"}
      </Btn>
    </div>
  );
}
